import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import type { LandingPagePreference } from 'API';
import { ColumnPreference, Paidol, PaidolUser, Role, User } from 'API';
// SEE-191: first-run checklist writes go to the CDK AppSync API (user.service).
import type { CompleteChecklistInput } from 'CDK_API';
import { getCurrentUser, signIn, signOut } from 'app/store/authSlice';
import type { RootState } from 'app/store/rootReducer';
import { setCompanySelection } from 'util/helpers';
import { isNotNullOrUndefined, isPaidolUser } from 'util/typeGuards';
import { userService } from 'app/services/user.service';
import { paidolService, type UpdatePaidolSettingsInput } from 'app/services/paidol.service';
import { migrateERPEndpointSettings } from 'app/pages/store/agaveSlice';

export interface UserCompaniesState {
  isUserCompaniesStateUnknown: boolean;
  isUserOnboarded: boolean;
  paidolUsers: Array<PaidolUser>;
  supportPaidolList: Array<Paidol>;
  selectedPaidolId: string;
  selectedPaidol?: Paidol;
  highnotePaymentCardIds: Array<string>;
  userRole?: Role;
  user?: User | null;
  searchPaidolUser?: Array<PaidolUser>;
  isSwitching: boolean;
  // Server-side switcher search results (SEE-862; extended to all users in
  // SEE-1035). The `searchPaidolsAsSupport` query scopes results to the caller
  // — support users get all companies, regular users get their own memberships
  // — sorted alphabetically across the full scoped set. The `support*` prefix
  // is kept only to avoid a wide rename. Populated on each switcher keystroke
  // and on scroll-to-bottom pagination; cleared on signOut and on paidol switch.
  supportSearchResults: Array<Paidol>;
  supportSearchLoading: boolean;
  supportSearchNextToken?: string | null;
  // The query string that produced the current results. Used as a fulfilled-
  // action guard: a slow response for a stale query gets discarded, so a fast
  // later keystroke can't be clobbered by an in-flight earlier one. `null`
  // is a sentinel for "no search session active" — set on init and on the
  // auth-reset matcher so an in-flight `{ query: '' }` dispatch from the
  // previous user can't pass the equality check ('' === '') and leak that
  // user's paidol list into the next sign-in on the same tab.
  supportSearchQuery: string | null;
}

export const initialState: UserCompaniesState = {
  isUserCompaniesStateUnknown: true,
  highnotePaymentCardIds: [],
  isUserOnboarded: false,
  paidolUsers: [],
  selectedPaidolId: '',
  selectedPaidol: undefined,
  user: undefined,
  searchPaidolUser: [],
  supportPaidolList: [],
  isSwitching: false,
  supportSearchResults: [],
  supportSearchLoading: false,
  supportSearchNextToken: null,
  supportSearchQuery: null,
};

export const getUserCompanies = createAsyncThunk<
  { paidolUsers: PaidolUser[]; supportPaidolList: Paidol[] },
  string
>('userCompanies/getUserCompanies', async (userId) => {
  const response = (await userService.listPaidolUserByUserId({ userId })) as {
    paidolUsersItems: PaidolUser[];
    paidolItems?: Paidol[];
  };

  const paidolUsers = response?.paidolUsersItems?.filter(isPaidolUser) || [];
  const supportPaidolList: Paidol[] = response?.paidolItems ?? [];

  return { paidolUsers, supportPaidolList };
});

/**
 * Thin state setter. To actually switch paidols (rotate the backend session,
 * refresh the JWT, and clear paidol-scoped state), dispatch
 * `savePaidolSessionId` instead. Kept as a thunk so existing
 * `setSelectedPaidolId.fulfilled` extraReducer listeners keep firing.
 */
export const setSelectedPaidolId = createAsyncThunk<{ paidolId: string; isSupport: boolean }, string>(
  'userCompanies/setSelectedPaidolId',
  async (paidol_id: string, { getState }) => {
    const state = getState() as any;
    const currentPaidolId = state.userCompanies.selectedPaidolId;

    if (currentPaidolId !== paidol_id) {
      setCompanySelection(paidol_id);
    }

    return { paidolId: paidol_id, isSupport: state.userCompanies.user?.is_support === true };
  }
);

export interface SearchPaidolsAsSupportArgs {
  query: string;
  limit?: number;
  nextToken?: string | null;
}

export const searchPaidolsAsSupport = createAsyncThunk(
  'userCompanies/searchPaidolsAsSupport',
  async ({ query, limit, nextToken }: SearchPaidolsAsSupportArgs) => {
    const result = await userService.searchPaidolsAsSupport({
      query,
      limit: limit ?? 25,
      nextToken: nextToken ?? null,
    });
    return {
      items: (result.items ?? []) as Paidol[],
      nextToken: result.nextToken ?? null,
    };
  }
);

/**
 * Hydrates `selectedPaidol` for support users who land on a paidol they don't
 * have a PaidolUser row for (e.g. arriving from a search-and-switch). The
 * regular path through `paidolUsers`/`supportPaidolList` doesn't fire for
 * those users now that we no longer receive the full Paidol firehose, so we
 * fetch the single Paidol explicitly via `getPaidolForSwitcher`
 * (graphql_custom/queries) — kept off the auto-generated import surface so
 * the eventual Amplify-to-CDK migration only has to touch one file. SEE-1232
 * did exactly that: the read now goes to CDK through `paidolService.getPaidol`.
 */
export const hydrateSelectedPaidolIfNeeded = createAsyncThunk(
  'userCompanies/hydrateSelectedPaidolIfNeeded',
  async (paidol_id: string) => {
    // SEE-1232 row n: transport flip only. The document, the argument and the returned
    // `Paidol` shape are unchanged; the service bridges the CDK payload to the API type.
    return paidolService.getPaidol<Paidol>(paidol_id);
  }
);

export interface UpdateMyPreferencesArgs {
  paidolUserId: string;
  paidolId: string;
  landingPagePreference?: LandingPagePreference;
  columnPreferences?: Array<{ paidolId: string; columns: ColumnPreference[] }>;
  widgetPreferences?: Array<{ widgetId: string; hidden: boolean }>;
}

// SEE-857 D2 — replaces the auto-gen `updatePaidolUser` mutation for the
// caller's own non-privileged preferences. `paidolUserId` is carried through
// to extraReducers for state merge; the Lambda resolves the target row from
// (JWT sub, paidolId) — never from any input field.
export const updateMyPreferences = createAsyncThunk(
  'userCompanies/updateMyPreferences',
  async (args: UpdateMyPreferencesArgs) => {
    const { paidolUserId: _paidolUserId, ...mutationInput } = args;
    return userService.updateMyPreferences(mutationInput);
  }
);

// SEE-857 D3 — admin-only write of paidol-level settings via the CDK
// AppSync `updatePaidolSettings` Lambda. The legacy thunk name stays for
// migration ergonomics; existing callers pass `{ id, ...settings }` and the
// thunk translates `id` → `paidolId` for the new mutation. The Lambda
// resolves the caller via JWT sub and runs `assert_admin` against `paidolId`.
//
// Migration notes:
// - The auto-gen `updatePaidol` mutation is dropped from the Amplify schema
//   in the same PR that lands this thunk (D3.5).
// - The `id` -> `paidolId` translation lives here so per-callsite code keeps
//   working unchanged. New code should call `paidolService.updatePaidolSettings`
//   directly with the `UpdatePaidolSettingsInput` shape.
// Legacy thunk argument: callers pass `{ id, ...settings }` and the thunk
// translates `id` → `paidolId` for the new mutation. Type alias keeps the
// shape declarative for callers (mirrors the dropped `UpdatePaidolInput`).
export type LegacyUpdatePaidolInput = { id: string } & Omit<UpdatePaidolSettingsInput, 'paidolId'>;

export const updatePaidol = createAsyncThunk(
  'userCompanies/updatePaidol',
  async (input: LegacyUpdatePaidolInput) => {
    const { id, ...rest } = input;
    return paidolService.updatePaidolSettings({ paidolId: id, ...rest });
  }
);

export interface SaveColumnPreferencesArgs {
  paidolUserId: string;
  columns: Array<{ columnId: string; visible: boolean; order: number }>;
}

export const saveColumnPreferences = createAsyncThunk(
  'userCompanies/saveColumnPreferences',
  async ({ paidolUserId, columns }: SaveColumnPreferencesArgs, { getState }) => {
    const state = getState() as RootState;
    const paidolUser = (state.userCompanies as UserCompaniesState).paidolUsers.find(
      (pu) => pu.id === paidolUserId
    ) as (PaidolUser & { columnPreferences?: Array<{ paidolId: string; columns: unknown[] }> }) | undefined;

    if (!paidolUser?.paidol_id) {
      return undefined;
    }

    const existingPrefs = paidolUser.columnPreferences ?? [];
    const paidolId = paidolUser.paidol_id;

    const otherPrefs = existingPrefs.filter((p) => p != null && p.paidolId !== paidolId);
    const updatedPrefs = [...otherPrefs, { paidolId, columns }];

    return userService.updateMyPreferences({ paidolId, columnPreferences: updatedPrefs });
  }
);

export interface SaveWidgetPreferencesArgs {
  paidolUserId: string;
  widgetPreferences: Array<{ widgetId: string; hidden: boolean }>;
}

export const saveWidgetPreferences = createAsyncThunk(
  'userCompanies/saveWidgetPreferences',
  async ({ paidolUserId, widgetPreferences }: SaveWidgetPreferencesArgs, { getState }) => {
    const state = getState() as RootState;
    const paidolUser = (state.userCompanies as UserCompaniesState).paidolUsers.find(
      (pu) => pu.id === paidolUserId
    );
    if (!paidolUser?.paidol_id) {
      return undefined;
    }

    return userService.updateMyPreferences({
      paidolId: paidolUser.paidol_id,
      widgetPreferences,
    });
  }
);

export const updateChecklist = createAsyncThunk(
  'userCompanies/completeFirstRun',
  async (input: CompleteChecklistInput) => {
    // SEE-191: served by the CDK first-run-resolver Lambda.
    return userService.completeFirstRunChecklist(input);
  }
);

const userCompaniesSlice = createSlice({
  name: 'userCompanies',
  initialState,
  reducers: {
    resetUserCompaniesSlice: () => initialState,
    setIsSwitching: (state, action: PayloadAction<boolean>) => {
      state.isSwitching = action.payload;
    },
    setSelectedPaidolProjectVisibilitySettings: (
      state,
      action: PayloadAction<{ isAllAvailable: boolean; ids: string[] }>
    ) => {
      if (state.selectedPaidol) {
        (state.selectedPaidol as any).projectVisibilitySettings = action.payload;
      }

      // Keep paidolUsers array in sync for the selected paidol
      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.paidolUsers[idx].paidol as any).projectVisibilitySettings = action.payload;
        }
      }
    },
    setSelectedPaidolGlCodeVisibilitySettings: (
      state,
      action: PayloadAction<{ isAllAvailable: boolean; ids: string[] }>
    ) => {
      if (state.selectedPaidol) {
        (state.selectedPaidol as any).glCodeVisibilitySettings = action.payload;
      }

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.selectedPaidol as any).glCodeVisibilitySettings = action.payload;
        }
      }
    },
    setSelectedPaidolLocationVisibilitySettings: (
      state,
      action: PayloadAction<{ isAllAvailable: boolean; ids: string[] }>
    ) => {
      if (state.selectedPaidol) {
        (state.selectedPaidol as any).locationVisibilitySettings = action.payload;
      }

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.paidolUsers[idx].paidol as any).locationVisibilitySettings = action.payload;
        }
      }
    },
    setSelectedPaidolCodingColumnsConfig: (
      state,
      action: PayloadAction<
        Record<
          string,
          {
            visibility: string;
            order?: number | null;
            customLabel?: string | null;
            defaultValue?: { id: string; label: string } | null;
            isRequired?: boolean;
          }
        >
      >
    ) => {
      if (state.selectedPaidol) {
        (state.selectedPaidol as any).codingColumnsConfig = action.payload;
      }

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.paidolUsers[idx].paidol as any).codingColumnsConfig = action.payload;
        }
      }
    },
    setSelectedPaidolCodingRequirements: (
      state,
      action: PayloadAction<{ isEnabled?: boolean; groups?: { columns: string[] }[] }>
    ) => {
      if (state.selectedPaidol) {
        (state.selectedPaidol as any).codingRequirements = action.payload;
      }

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.paidolUsers[idx].paidol as any).codingRequirements = action.payload;
        }
      }
    },
    setSelectedPaidolApprovalWorkflowSettings: (
      state,
      action: PayloadAction<{ isEnabled?: boolean; approvalLevels?: number | null }>
    ) => {
      // Merge, not replace: the payload carries only the fields its caller saved, and the
      // backend partial-merges too — replacing drops groupApprovalsEnabled from the store.
      const merge = (paidol: Paidol) => {
        paidol.approvalWorkflowSettings = {
          __typename: 'ApprovalWorkflowSettings',
          ...paidol.approvalWorkflowSettings,
          ...action.payload,
        };
      };

      if (state.selectedPaidol) merge(state.selectedPaidol);

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        const paidol = idx >= 0 ? state.paidolUsers[idx].paidol : null;
        if (paidol) merge(paidol);
      }
    },
    setSelectedPaidolThresholds: (
      state,
      action: PayloadAction<{
        low_balance_alert_threshold: number | null;
        low_balance_auto_funding_amount: number | null;
        low_balance_auto_funding_threshold: number | null;
      }>
    ) => {
      if (state.selectedPaidol) {
        state.selectedPaidol.low_balance_alert_threshold = action.payload.low_balance_alert_threshold;
        state.selectedPaidol.low_balance_auto_funding_amount = action.payload.low_balance_auto_funding_amount;
        state.selectedPaidol.low_balance_auto_funding_threshold =
          action.payload.low_balance_auto_funding_threshold;
      }

      if (state.selectedPaidolId) {
        const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
        if (idx >= 0 && state.paidolUsers[idx].paidol) {
          (state.paidolUsers[idx].paidol as any).low_balance_alert_threshold =
            action.payload.low_balance_alert_threshold;
          (state.paidolUsers[idx].paidol as any).low_balance_auto_funding_amount =
            action.payload.low_balance_auto_funding_amount;
          (state.paidolUsers[idx].paidol as any).low_balance_auto_funding_threshold =
            action.payload.low_balance_auto_funding_threshold;
        }
      }
    },
    // SEE-272: a Control Center credit limit change (cents), so pages reading the selected
    // company (the customer Credit page) show it without a reload.
    setSelectedPaidolCreditLimit: (state, action: PayloadAction<number | null>) => {
      if (state.selectedPaidol) {
        state.selectedPaidol.creditLimit = action.payload;
      }

      if (state.selectedPaidolId) {
        const paidol = state.paidolUsers.find((pu) => pu.paidol?.id === state.selectedPaidolId)?.paidol;
        if (paidol) {
          paidol.creditLimit = action.payload;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserCompanies.fulfilled, (state, action) => {
      state.isUserCompaniesStateUnknown = false;
      state.isUserOnboarded = action.payload && action.payload.paidolUsers.length > 0;
      state.paidolUsers = action.payload.paidolUsers;
      state.supportPaidolList = action.payload.supportPaidolList;

      // Defensive optional-chain: in practice every user (including support
      // employees) has at least one PaidolUser row, but a `paidolUsers[0]`
      // crash here would leave the app stuck on a blank spinner with no way
      // out, so guard the access.
      // eslint-disable-next-line no-restricted-syntax -- SEE-1352: index is irrelevant here; every PaidolUser row for one user carries the same `user` object, so any row yields the same value.
      state.user = action.payload.paidolUsers[0]?.user ?? null;
    });

    builder.addCase(updateMyPreferences.fulfilled, (state, action) => {
      // updateMyPreferences resolves to the parsed PaidolUser dict from the
      // Lambda's BasicHTTPResponse body. Merge into the matching row keyed by
      // arg.paidolUserId (the dict has `id` too, but we trust the arg).
      const payload = action.payload as
        | (Partial<PaidolUser> & { id?: string; paidol?: Paidol | null })
        | undefined;
      const targetId = action.meta.arg.paidolUserId;
      const idx = state.paidolUsers.findIndex((pu) => pu.id === targetId);
      if (idx >= 0 && payload) {
        const existing = state.paidolUsers[idx];
        state.paidolUsers[idx] = {
          ...existing,
          ...payload,
          paidol: payload.paidol ?? existing.paidol,
        } as PaidolUser;
      }
    });

    builder.addCase(saveColumnPreferences.fulfilled, (state, action) => {
      const { paidolUserId, columns } = action.meta.arg;
      const idx = state.paidolUsers.findIndex((pu) => pu.id === paidolUserId);
      if (idx >= 0) {
        const paidolId = state.paidolUsers[idx].paidol_id ?? '';
        const existing =
          (
            state.paidolUsers[idx] as PaidolUser & {
              columnPreferences?: Array<{ paidolId: string; columns: unknown[] }>;
            }
          ).columnPreferences ?? [];
        const otherPrefs = existing.filter((p) => p != null && p.paidolId !== paidolId);
        Object.assign(state.paidolUsers[idx], {
          columnPreferences: [...otherPrefs, { paidolId, columns }],
        });
      }
    });

    builder.addCase(saveWidgetPreferences.fulfilled, (state, action) => {
      const { paidolUserId, widgetPreferences } = action.meta.arg;
      const idx = state.paidolUsers.findIndex((pu) => pu.id === paidolUserId);
      if (idx >= 0) {
        Object.assign(state.paidolUsers[idx], { widgetPreferences });
      }
    });

    builder.addCase(updatePaidol.fulfilled, (state, action) => {
      // SEE-857 D3 — payload is the parsed Paidol dict from the CDK Lambda's
      // BasicHTTPResponse body (full row from PynamoDB). Merge into state and
      // keep the paidolUsers entry in sync. We still strip codingColumnsConfig
      // from the merge since the dedicated setter dispatches it separately.
      const payload = action.payload as Record<string, unknown> | undefined;
      if (payload && state.selectedPaidol) {
        const { codingColumnsConfig: _omitted, ...payloadRest } = payload as Paidol & {
          codingColumnsConfig?: unknown;
        };

        state.selectedPaidol = {
          ...state.selectedPaidol,
          ...payloadRest,
        };

        // Keep paidolUsers array in sync
        if (state.selectedPaidolId) {
          const idx = state.paidolUsers.findIndex((pu) => pu.paidol?.id === state.selectedPaidolId);
          if (idx >= 0 && state.paidolUsers[idx].paidol) {
            state.paidolUsers[idx].paidol = {
              ...state.paidolUsers[idx].paidol!,
              ...payloadRest,
            };
          }
        }
      }
    });

    builder.addCase(setSelectedPaidolId.fulfilled, (state, action) => {
      const { paidolId, isSupport } = action.payload;
      const selectedPaidolUser = state.paidolUsers.find((paidolUser) => {
        return paidolUser.paidol?.id === paidolId;
      });

      if (selectedPaidolUser?.paidol) {
        const paymentCardsArray = (selectedPaidolUser as any).paymentCards;
        state.highnotePaymentCardIds =
          paymentCardsArray
            ?.filter((i: any) => isNotNullOrUndefined(i))
            .map((i: any) => i.highnotePaymentCardID)
            .filter((id: unknown): id is string => typeof id === 'string') ?? [];
        state.selectedPaidol = {
          ...selectedPaidolUser.paidol,
          enablePCards: selectedPaidolUser.paidol.enablePCards ?? false,
          low_balance_alert_threshold: selectedPaidolUser.paidol.low_balance_alert_threshold ?? 0,
          isConstructionType: selectedPaidolUser.paidol.isConstructionType ?? false,
          isAgaveClient: selectedPaidolUser.paidol.isAgaveClient ?? false,
          isProcoreClient: selectedPaidolUser.paidol.isProcoreClient ?? false,
          agaveAccountToken: selectedPaidolUser.paidol.agaveAccountToken ?? '',
          agave_data_sync_in_progress: selectedPaidolUser.paidol.agave_data_sync_in_progress ?? false,
        };
        state.userRole = selectedPaidolUser.roles ?? Role.GROUP_ADMINISTRATOR;
      } else {
        // No membership row: support legitimately lands here and gets the admin
        // view; anyone else means the lookup failed, so fail closed to no role.
        state.userRole = isSupport ? Role.ADMINISTRATOR : undefined;
        // Support-user fallback. supportPaidolList stays for compat with any
        // older flows that still populate it; supportSearchResults is the
        // post-SEE-862 source of truth (results from the typed search). If
        // neither has the active paidol (e.g. fresh page reload after a
        // search-and-switch), the AuthProvider effect dispatches
        // hydrateSelectedPaidolIfNeeded to fill it in via getPaidol.
        const selectedPaidol =
          state.supportPaidolList.find((paidol) => paidol.id === paidolId) ||
          state.supportSearchResults.find((paidol) => paidol.id === paidolId);
        state.highnotePaymentCardIds = [];

        if (selectedPaidol) {
          state.selectedPaidol = {
            ...selectedPaidol,
            enablePCards: selectedPaidol.enablePCards ?? false,
            low_balance_alert_threshold: selectedPaidol.low_balance_alert_threshold ?? 0,
            isConstructionType: selectedPaidol.isConstructionType ?? false,
            isAgaveClient: selectedPaidol.isAgaveClient ?? false,
            isProcoreClient: selectedPaidol.isProcoreClient ?? false,
            agaveAccountToken: selectedPaidol.agaveAccountToken ?? '',
            agave_data_sync_in_progress: selectedPaidol.agave_data_sync_in_progress ?? false,
          };
        } else if (state.selectedPaidol?.id !== paidolId) {
          // Chain-switch fix: when an is_support user moves from one non-member
          // paidol (e.g. Alderaan) to another non-member paidol (e.g. Bespin)
          // and the new paidol isn't in supportSearchResults, leaving the
          // previous paidol in state would mask the new selection — the
          // hydrate effect's `!selectedPaidol` gate would skip, and downstream
          // components would render with stale data tied to the old paidol.
          // Clear it so hydrateSelectedPaidolIfNeeded fires for the new id.
          state.selectedPaidol = undefined;
        }
      }

      state.selectedPaidolId = paidolId;
    });

    builder.addCase(searchPaidolsAsSupport.pending, (state, action) => {
      state.supportSearchLoading = true;
      // A fresh dispatch (no nextToken in arg) means the user changed the
      // query or just opened the picker — clear stale results and remember
      // the new query so the fulfilled handler can guard against an earlier
      // in-flight response landing on top of newer ones.
      if (!action.meta.arg.nextToken) {
        state.supportSearchResults = [];
        state.supportSearchNextToken = null;
        state.supportSearchQuery = action.meta.arg.query;
      }
    });
    builder.addCase(searchPaidolsAsSupport.fulfilled, (state, action) => {
      // Discard stale-query responses (e.g. a slow page-1 for "doug" landing
      // after the user has typed "douglas"). Pagination requests carry the
      // same query as the current state, so they pass through. The null
      // check rejects responses that survived an auth reset — without it, an
      // in-flight `{ query: '' }` could re-populate results after signOut.
      if (state.supportSearchQuery === null) return;
      if (action.meta.arg.query !== state.supportSearchQuery) return;
      state.supportSearchLoading = false;
      state.supportSearchNextToken = action.payload.nextToken;
      if (action.meta.arg.nextToken) {
        // Pagination — append, dedupe by id so a server-side overlap or a
        // racing duplicate request can't double-render the same row.
        const existingIds = new Set(state.supportSearchResults.map((p) => p.id));
        for (const item of action.payload.items) {
          if (!existingIds.has(item.id)) {
            state.supportSearchResults.push(item);
            existingIds.add(item.id);
          }
        }
      } else {
        state.supportSearchResults = action.payload.items;
      }
    });
    builder.addCase(searchPaidolsAsSupport.rejected, (state, action) => {
      if (state.supportSearchQuery === null) return;
      if (action.meta.arg.query !== state.supportSearchQuery) return;
      state.supportSearchLoading = false;
    });

    builder.addCase(hydrateSelectedPaidolIfNeeded.fulfilled, (state, action) => {
      const paidol = action.payload;
      // Apply the response when it matches the current selection. Previously
      // this also required `!state.selectedPaidol` (only fill an empty slot),
      // which left chain-switches pinned to the prior non-member paidol's
      // data — selectedPaidolId would be the new paidol while selectedPaidol
      // was still the old one. Now we also overwrite when the existing
      // selectedPaidol's id doesn't match selectedPaidolId.
      const idMatches = paidol && state.selectedPaidolId === paidol.id;
      const slotEmptyOrStale = !state.selectedPaidol || state.selectedPaidol.id !== paidol?.id;
      if (idMatches && slotEmptyOrStale) {
        state.selectedPaidol = {
          ...paidol,
          enablePCards: paidol.enablePCards ?? false,
          low_balance_alert_threshold: paidol.low_balance_alert_threshold ?? 0,
          isConstructionType: paidol.isConstructionType ?? false,
          isAgaveClient: paidol.isAgaveClient ?? false,
          isProcoreClient: paidol.isProcoreClient ?? false,
          agaveAccountToken: paidol.agaveAccountToken ?? '',
          agave_data_sync_in_progress: paidol.agave_data_sync_in_progress ?? false,
        };
      }
    });

    builder.addCase(updateChecklist.fulfilled, (state, action) => {
      const { data } = action.meta.arg;

      if (state.selectedPaidol?.firstRunChecklist) {
        state.selectedPaidol.firstRunChecklist = {
          ...state.selectedPaidol.firstRunChecklist,
          [data.firstRunValue]: true,
        };
      }
    });

    builder.addCase(migrateERPEndpointSettings.fulfilled, (state, action) => {
      // Only update if migration actually occurred
      if (action.payload?.migrated && action.payload?.paidol?.erpProjectsEndpointSetting) {
        if (state.selectedPaidol && state.selectedPaidol.id === action.payload.paidol.id) {
          state.selectedPaidol.erpProjectsEndpointSetting = action.payload.paidol.erpProjectsEndpointSetting;
        }
      }
    });

    builder.addMatcher(isAnyOf(getUserCompanies.pending, signIn.pending), (state) => {
      state.isUserCompaniesStateUnknown = true;
    });

    builder.addMatcher(
      isAnyOf(getUserCompanies.rejected, signIn.rejected, getCurrentUser.rejected, signOut.fulfilled),
      (state) => {
        state.isUserCompaniesStateUnknown = false;
        state.isUserOnboarded = false;
        state.paidolUsers = [];
        state.selectedPaidol = undefined;
        state.selectedPaidolId = '';
        state.user = undefined;
        state.userRole = undefined;
        state.searchPaidolUser = [];
        // Clear support-search state on signOut/auth-error so a subsequent
        // user can't briefly see the previous session's paidol list.
        // `supportSearchQuery = null` is load-bearing: a fulfilled response
        // from an in-flight `{ query: '' }` dispatched before the reset
        // would otherwise still match the initial-state '' and write the
        // previous user's results into the new session's switcher.
        state.supportSearchResults = [];
        state.supportSearchNextToken = null;
        state.supportSearchQuery = null;
        state.supportSearchLoading = false;
      }
    );
  },
});

export const {
  resetUserCompaniesSlice,
  setIsSwitching,
  setSelectedPaidolProjectVisibilitySettings,
  setSelectedPaidolGlCodeVisibilitySettings,
  setSelectedPaidolLocationVisibilitySettings,
  setSelectedPaidolCodingColumnsConfig,
  setSelectedPaidolCodingRequirements,
  setSelectedPaidolApprovalWorkflowSettings,
  setSelectedPaidolThresholds,
  setSelectedPaidolCreditLimit,
} = userCompaniesSlice.actions;

export const selectUserCompanies = (state: RootState) => state.userCompanies as UserCompaniesState;
export const selectUserPaidolUsers = (state: RootState) => state.userCompanies.paidolUsers;

export const selectCurrentPaidolUser = (state: RootState): PaidolUser | undefined => {
  const uc = state.userCompanies as UserCompaniesState;
  return uc.paidolUsers.find(
    (p) => p.paidol?.id === uc.selectedPaidolId || p.paidol_id === uc.selectedPaidolId
  );
};

export const selectCurrentPaidolUserId = (state: RootState): string | undefined =>
  selectCurrentPaidolUser(state)?.id;

// One shared empty list: a fresh [] per call fails useAppSelector's equality check on
// every dispatch, re-rendering every caller and rebuilding the transactions table columns.
const NO_COLUMN_PREFERENCES: ColumnPreference[] = [];

/**
 * Selects the saved column preferences for the current paidol from the PaidolUser record.
 * Returns an empty array if no preferences are saved.
 */
export const selectColumnPreferences = (
  state: RootState,
  paidolId: string | null | undefined
): ColumnPreference[] => {
  if (!paidolId) return NO_COLUMN_PREFERENCES;
  const uc = state.userCompanies as UserCompaniesState;
  const paidolUser = uc.paidolUsers.find((pu) => pu.paidol?.id === paidolId || pu.paidol_id === paidolId) as
    | (PaidolUser & { columnPreferences?: Array<{ paidolId: string; columns: ColumnPreference[] }> })
    | undefined;
  const entry = paidolUser?.columnPreferences?.find((p) => p != null && p.paidolId === paidolId);
  return (entry?.columns ?? NO_COLUMN_PREFERENCES) as ColumnPreference[];
};

export const selectLandingPagePreference = (state: RootState): LandingPagePreference | null | undefined => {
  const uc = state.userCompanies as UserCompaniesState;
  const pu = uc.paidolUsers.find(
    (p) => p.paidol?.id === uc.selectedPaidolId || p.paidol_id === uc.selectedPaidolId
  );
  return pu?.landingPagePreference ?? null;
};

export default userCompaniesSlice.reducer;
